import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DynamicSelect } from './DynamicSelect';
import { useCreateDevice, useUpdateDevice } from '@/hooks/useDevices';
import { trpc } from '@/lib/trpc';
import type { Device } from '../../../../shared/types';
import { Laptop, User, Tag } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { es } from 'date-fns/locale';

// Zod schema para validación
const deviceSchema = z.object({
  fechaCompra: z.string().optional(),
  proveedor: z.string().optional(),
  numFactura: z.string().optional(),
  costo: z.coerce.number().positive().optional().or(z.literal('')),
  linkFactura: z.string().url({ message: 'Debe ser una URL válida' }).optional().or(z.literal('')),
  clapBase: z.string().min(1, 'ClapBase es requerido'),
  nroReferencia: z.string().optional(),
  categoria: z.string().min(1, 'Categoría es requerida'),
  modelo: z.string().min(1, 'Modelo es requerido'),
  nroModelo: z.string().optional(),
  nroSerie: z.string().min(1, 'Número de serie es requerido'),
  procesador: z.string().optional(),
  ramGB: z.coerce.number().positive().optional().or(z.literal('')),
  discoGB: z.coerce.number().positive().optional().or(z.literal('')),
  status: z.string().min(1, 'Status es requerido'),
  owner: z.string().optional(),
  comments: z.string().optional(),
  quipteamsOrderId: z.string().optional(),
});

type DeviceFormData = z.infer<typeof deviceSchema>;

interface DeviceFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  device?: Device;
  /** Pre-fill values for a NEW device (e.g. from a QuipTeams suggestion) */
  initialValues?: Partial<DeviceFormData>;
}

export function DeviceFormDialog({ open, onOpenChange, device, initialValues }: DeviceFormDialogProps) {
  const { t } = useTranslation();
  const isEditing = !!device;
  const createDevice = useCreateDevice();
  const updateDevice = useUpdateDevice();

  const { data: history = [] } = trpc.inventory.getDeviceHistory.useQuery(
    device?.deviceId ?? '',
    { enabled: isEditing && open && !!device?.deviceId }
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
    control,
  } = useForm<DeviceFormData>({
    resolver: zodResolver(deviceSchema),
  });

  // Reset form cuando cambia el device o se abre/cierra
  useEffect(() => {
    if (device && open) {
      reset({
        fechaCompra: device.fechaCompra || '',
        proveedor: device.proveedor || '',
        numFactura: device.numFactura || '',
        costo: device.costo || ('' as any),
        linkFactura: device.linkFactura || '',
        clapBase: device.clapBase || '',
        nroReferencia: device.nroReferencia || '',
        categoria: device.categoria || '',
        modelo: device.modelo || '',
        nroModelo: device.nroModelo || '',
        nroSerie: device.nroSerie || '',
        procesador: device.procesador || '',
        ramGB: device.ramGB || ('' as any),
        discoGB: device.discoGB || ('' as any),
        status: device.status || '',
        owner: device.owner || '',
        comments: device.comments || '',
        quipteamsOrderId: device.quipteamsOrderId || '',
      });
    } else if (!device && open) {
      reset({
        fechaCompra: '',
        proveedor: '',
        numFactura: '',
        costo: '' as any,
        linkFactura: '',
        clapBase: '',
        nroReferencia: '',
        categoria: '',
        modelo: '',
        nroModelo: '',
        nroSerie: '',
        procesador: '',
        ramGB: '' as any,
        discoGB: '' as any,
        status: '',
        owner: '',
        comments: '',
        quipteamsOrderId: '',
        // Prefill from a QuipTeams suggestion (only the fields it provides)
        ...initialValues,
      });
    }
  }, [device, open, reset, initialValues]);

  // Auto-completar Nro Referencia cuando cambia ClapBase
  const clapBaseValue = watch('clapBase');
  useEffect(() => {
    if (!isEditing && clapBaseValue && open) {
      const fetchNextReference = async () => {
        try {
          const session = await fetchAuthSession();
          const token = session.tokens?.idToken?.toString();
          const response = await fetch(
            `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/admin/inventory/next-reference?clapBase=${encodeURIComponent(clapBaseValue)}`,
            {
              headers: {
                Authorization: token ? `Bearer ${token}` : '',
              },
            }
          );
          const data = await response.json();
          if (data.success) {
            setValue('nroReferencia', data.data.nextReference);
          }
        } catch (error) {
          console.error('Error fetching next reference:', error);
        }
      };
      fetchNextReference();
    }
  }, [clapBaseValue, isEditing, open, setValue]);

  // Fix 04/09: el form es largo y los errores de validación quedaban fuera de
  // vista — el botón "Crear" parecía muerto. Ahora un toast lista qué falta.
  const FIELD_LABELS: Record<string, string> = {
    fechaCompra: 'Fecha de compra', proveedor: 'Proveedor', numFactura: 'N° factura',
    costo: 'Costo', linkFactura: 'Link factura', clapBase: 'ClapBase',
    nroReferencia: 'N° referencia', categoria: 'Categoría', modelo: 'Modelo',
    nroSerie: 'N° de serie', procesador: 'Procesador', ramGB: 'RAM', discoGB: 'Disco',
    status: 'Status', owner: 'Owner', comments: 'Comentarios',
  };
  const onInvalid = (errs: Record<string, unknown>) => {
    const fields = Object.keys(errs).map((k) => FIELD_LABELS[k] ?? k);
    toast.error(`Faltan campos o hay errores: ${fields.join(', ')}`);
  };

  const onSubmit = async (data: DeviceFormData) => {
    try {
      const payload: any = {
        ...data,
        ramGB: data.ramGB === '' ? undefined : Number(data.ramGB),
        discoGB: data.discoGB === '' ? undefined : Number(data.discoGB),
        costo: data.costo === '' ? undefined : Number(data.costo),
      };

      if (isEditing && device) {
        await updateDevice.mutateAsync({
          deviceId: device.deviceId,
          updates: payload,
        });
      } else {
        await createDevice.mutateAsync(payload);
      }

      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const getHistoryIcon = (eventType: string) => {
    switch (eventType) {
      case 'created': return <Laptop className="h-4 w-4 text-blue-500" />;
      case 'owner_changed': return <User className="h-4 w-4 text-green-500" />;
      case 'status_changed': return <Tag className="h-4 w-4 text-orange-500" />;
      default: return <Tag className="h-4 w-4 text-gray-400" />;
    }
  };

  const getHistoryDescription = (entry: typeof history[number]) => {
    switch (entry.eventType) {
      case 'created':
        return `Dispositivo creado (${entry.newValue || ''})`;
      case 'owner_changed':
        return `Owner: ${entry.previousValue} → ${entry.newValue}`;
      case 'status_changed':
        return `Status: ${entry.previousValue} → ${entry.newValue}`;
      default:
        return entry.eventType;
    }
  };

  const SectionHeader = ({ label }: { label: string }) => (
    <div className="pt-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</h3>
      <div className="mt-1.5 border-b" />
    </div>
  );

  const deviceForm = (
    <form onSubmit={handleSubmit(onSubmit as any, onInvalid)} className="space-y-5">
      <SectionHeader label={t('devices.form.section_purchase')} />
      {/* Fecha de Compra y Proveedor */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="fechaCompra">{t('devices.form.purchase_date')}</Label>
          <Input id="fechaCompra" type="date" {...register('fechaCompra')} />
          {errors.fechaCompra && (
            <p className="text-sm text-destructive">{errors.fechaCompra.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label>{t('devices.form.supplier')}</Label>
          <Controller
            name="proveedor"
            control={control}
            render={({ field }) => (
              <DynamicSelect
                optionType="proveedor"
                value={field.value}
                onValueChange={field.onChange}
                placeholder={t('devices.form.select_supplier')}
              />
            )}
          />
          {errors.proveedor && (
            <p className="text-sm text-destructive">{errors.proveedor.message}</p>
          )}
        </div>
      </div>

      {/* Num. Factura y Costo */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="numFactura">{t('devices.form.invoice_num')}</Label>
          <Input id="numFactura" {...register('numFactura')} placeholder={t('devices.form.invoice_num_placeholder')} />
          {errors.numFactura && (
            <p className="text-sm text-destructive">{errors.numFactura.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="costo">{t('devices.form.cost')}</Label>
          <Input id="costo" type="number" step="0.01" {...register('costo')} placeholder={t('devices.form.cost_placeholder')} />
          {errors.costo && (
            <p className="text-sm text-destructive">{errors.costo.message}</p>
          )}
        </div>
      </div>

      {/* Link Factura */}
      <div className="space-y-2">
        <Label htmlFor="linkFactura">{t('devices.form.invoice_link')}</Label>
        <Input id="linkFactura" type="url" {...register('linkFactura')} placeholder="https://..." />
        {errors.linkFactura && (
          <p className="text-sm text-destructive">{errors.linkFactura.message}</p>
        )}
      </div>

      <SectionHeader label={t('devices.form.section_identification')} />
      {/* ClapBase y Nro Referencia */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label>{t('devices.form.clapbase_label')} <span className="text-destructive">*</span></Label>
          <Controller
            name="clapBase"
            control={control}
            render={({ field }) => (
              <DynamicSelect
                optionType="clapbase"
                value={field.value}
                onValueChange={field.onChange}
                placeholder={t('devices.form.select_clapbase')}
              />
            )}
          />
          {errors.clapBase && (
            <p className="text-sm text-destructive">{errors.clapBase.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="nroReferencia">{t('devices.form.reference')}</Label>
          <Input
            id="nroReferencia"
            {...register('nroReferencia')}
            placeholder={isEditing ? "ej: B10" : t('devices.form.reference_auto')}
            disabled={!isEditing}
            className={!isEditing ? 'bg-muted cursor-not-allowed' : ''}
          />
          {!isEditing && (
            <p className="text-xs text-muted-foreground">
              {t('devices.form.reference_hint')}
            </p>
          )}
        </div>
      </div>

      {/* Categoría y Modelo */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label>{t('devices.form.category')} <span className="text-destructive">*</span></Label>
          <Controller
            name="categoria"
            control={control}
            render={({ field }) => (
              <DynamicSelect
                optionType="categoria"
                value={field.value}
                onValueChange={field.onChange}
                placeholder={t('devices.form.select_category')}
              />
            )}
          />
          {errors.categoria && (
            <p className="text-sm text-destructive">{errors.categoria.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label>{t('devices.form.model')} <span className="text-destructive">*</span></Label>
          <Controller
            name="modelo"
            control={control}
            render={({ field }) => (
              <DynamicSelect
                optionType="modelo"
                value={field.value}
                onValueChange={field.onChange}
                placeholder={t('devices.form.select_model')}
              />
            )}
          />
          {errors.modelo && (
            <p className="text-sm text-destructive">{errors.modelo.message}</p>
          )}
        </div>
      </div>

      {/* Nro de Modelo */}
      <div className="space-y-2">
        <Label htmlFor="nroModelo">{t('devices.form.model_number')}</Label>
        <Input id="nroModelo" {...register('nroModelo')} placeholder="ej: MK183LL/A" />
      </div>

      {/* Nro de Serie */}
      <div className="space-y-2">
        <Label htmlFor="nroSerie">
          {t('devices.form.serial')} <span className="text-destructive">*</span>
        </Label>
        <Input id="nroSerie" {...register('nroSerie')} placeholder={t('devices.form.serial_placeholder')} />
        {errors.nroSerie && (
          <p className="text-sm text-destructive">{errors.nroSerie.message}</p>
        )}
      </div>

      <SectionHeader label={t('devices.form.section_hardware')} />
      {/* Procesador, RAM, Disco */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="space-y-2">
          <Label htmlFor="procesador">{t('devices.form.processor')}</Label>
          <Input id="procesador" {...register('procesador')} placeholder={t('devices.form.processor_placeholder')} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="ramGB">{t('devices.form.ram')}</Label>
          <Input id="ramGB" type="number" {...register('ramGB')} placeholder={t('devices.form.ram_placeholder')} />
          {errors.ramGB && (
            <p className="text-sm text-destructive">{errors.ramGB.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="discoGB">{t('devices.form.disk')}</Label>
          <Input id="discoGB" type="number" {...register('discoGB')} placeholder={t('devices.form.disk_placeholder')} />
          {errors.discoGB && (
            <p className="text-sm text-destructive">{errors.discoGB.message}</p>
          )}
        </div>
      </div>

      <SectionHeader label={t('devices.form.section_assignment')} />
      {/* Status */}
      <div className="space-y-2">
        <Label>{t('devices.form.status')} <span className="text-destructive">*</span></Label>
        <Controller
          name="status"
          control={control}
          render={({ field }) => (
            <DynamicSelect
              optionType="status"
              value={field.value}
              onValueChange={field.onChange}
              placeholder={t('devices.form.select_status')}
            />
          )}
        />
        {errors.status && (
          <p className="text-sm text-destructive">{errors.status.message}</p>
        )}
      </div>

      {/* Owner */}
      <div className="space-y-2">
        <Label htmlFor="owner">{t('devices.form.owner')}</Label>
        <Input id="owner" {...register('owner')} placeholder={t('devices.form.owner_placeholder')} />
      </div>

      {/* Comments */}
      <div className="space-y-2">
        <Label htmlFor="comments">{t('devices.form.comments')}</Label>
        <Textarea
          id="comments"
          {...register('comments')}
          placeholder={t('devices.form.comments_placeholder')}
          rows={3}
        />
      </div>

      {/* QuipTeams Order ID */}
      <div className="space-y-2">
        <Label htmlFor="quipteamsOrderId">{t('devices.form.quipteams_order')}</Label>
        <Input
          id="quipteamsOrderId"
          {...register('quipteamsOrderId')}
          // i18n-ok: id técnico de ejemplo
          placeholder="ej: qt_abc123"
        />
        <p className="text-xs text-muted-foreground">{t('devices.form.quipteams_order_hint')}</p>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          {t('devices.form.cancel')}
        </Button>
        <Button
          type="submit"
          disabled={createDevice.isPending || updateDevice.isPending}
        >
          {createDevice.isPending || updateDevice.isPending
            ? t('devices.form.saving')
            : isEditing
            ? t('devices.form.update')
            : t('devices.form.create')}
        </Button>
      </DialogFooter>
    </form>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? t('devices.form.title_edit') : t('devices.form.title_new')}</DialogTitle>
        </DialogHeader>

        {isEditing ? (
          <Tabs defaultValue="datos">
            <TabsList className="mb-4">
              <TabsTrigger value="datos">{t('devices.form.tabs.data')}</TabsTrigger>
              <TabsTrigger value="movimientos">{t('devices.form.tabs.movements')}</TabsTrigger>
            </TabsList>

            <TabsContent value="datos">
              {deviceForm}
            </TabsContent>

            <TabsContent value="movimientos">
              <div className="space-y-3 min-h-[200px]">
                {history.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    {t('devices.form.no_movements')}
                  </p>
                ) : (
                  history.map((entry) => (
                    <div key={entry.historyId} className="flex items-start gap-3 p-3 rounded-lg border bg-muted/20">
                      <div className="mt-0.5 shrink-0">
                        {getHistoryIcon(entry.eventType)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{getHistoryDescription(entry)}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{entry.changedBy}</p>
                      </div>
                      <p className="text-xs text-muted-foreground shrink-0">
                        {formatDistanceToNow(new Date(entry.createdAt), {
                          addSuffix: true,
                          locale: es,
                        })}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>
          </Tabs>
        ) : (
          deviceForm
        )}
      </DialogContent>
    </Dialog>
  );
}
